/**
 * AI Platform API client (EP-44) -- Platform Management's AI Models tab.
 *
 * Thin client for /api/platform/ai: providers (read-only; they come from the
 * deployment's ai-providers.yaml), the model catalog (discovery, capabilities,
 * accreditation), the accreditation gate, and the per-usage assignments
 * (default + overrides, fallback chains, history, rollback).
 *
 * Writes carry `expected_version` (optimistic concurrency): a 409 means someone
 * else changed the assignment first -- reload and retry. Errors are thrown with
 * a readable message (services/apiError.js); 422 details list every problem in
 * `error.detail.errors`.
 *
 * Auth follows WeaponeeringAPIClient's pattern (hooks/useApi.js wires the token).
 */

import { apiErrorFromResponse } from '../apiError';

const enc = encodeURIComponent;

class AiPlatformAPIClient {
  constructor(baseURL = null) {
    const baseUrl = baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    this.baseURL = `${baseUrl}/api/platform/ai`;
    this.authToken = null;
    this.tokenProvider = null;
  }

  setAuthToken(token) { this.authToken = token; }
  clearAuthToken() { this.authToken = null; }
  setTokenProvider(fn) { this.tokenProvider = fn; }
  clearTokenProvider() { this.tokenProvider = null; }

  async resolveToken() {
    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          this.authToken = token;
          return token;
        }
      } catch (err) {
        console.warn('Could not obtain a fresh access token:', err?.message || err);
      }
    }
    return this.authToken;
  }

  async request(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const token = await this.resolveToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}${endpoint}`, { ...options, headers });
    if (!response.ok) throw await apiErrorFromResponse(response);
    if (response.status === 204) return null;
    return response.json();
  }

  // ---- read ----
  getProviders() { return this.request('/providers'); }
  getModels() { return this.request('/models'); }
  getSettings() { return this.request('/settings'); }
  getUsages() { return this.request('/usages'); }
  getHistory(scope) { return this.request(`/assignments/${enc(scope)}/history`); }

  // ---- write ----
  discoverModels() { return this.request('/models/discover', { method: 'POST' }); }

  setModelCapabilities(providerId, modelId, capabilities) {
    return this.request(`/models/${enc(providerId)}/${modelId}/capabilities`, {
      method: 'PUT', body: JSON.stringify({ capabilities }),
    });
  }

  setModelAccreditation(providerId, modelId, accredited) {
    return this.request(`/models/${enc(providerId)}/${modelId}/accreditation`, {
      method: 'PUT', body: JSON.stringify({ accredited }),
    });
  }

  setAccreditationRequired(required) {
    return this.request('/settings/accreditation-required', {
      method: 'PUT', body: JSON.stringify({ accreditation_required: required }),
    });
  }

  /** scope: 'default' or a usage id. expectedVersion: the version edited (null = creating an override). */
  putAssignment(scope, { selector, fallback, fallbackEnabled, expectedVersion, note }) {
    return this.request(`/assignments/${enc(scope)}`, {
      method: 'PUT',
      body: JSON.stringify({
        selector, fallback, fallback_enabled: fallbackEnabled, expected_version: expectedVersion, note: note || null,
      }),
    });
  }

  deleteOverride(scope, expectedVersion, note) {
    return this.request(`/assignments/${enc(scope)}`, {
      method: 'DELETE', body: JSON.stringify({ expected_version: expectedVersion, note: note || null }),
    });
  }

  rollback(scope, toVersion, expectedVersion, note) {
    return this.request(`/assignments/${enc(scope)}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ to_version: toVersion, expected_version: expectedVersion, note: note || null }),
    });
  }
}

export default new AiPlatformAPIClient();
